import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Gift, IdCard, MessageSquareText, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { DatePicker } from '@/shared/ui/date-picker';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SegmentedControl, type SegmentedOption } from '@/shared/ui/segmented-control';
import { AnimatedIcon, animatedIcon, type IconMotion } from '@/shared/components/animated-icon';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';
import { cn } from '@/shared/lib/cn';
import { usePassbook } from '@/shared/lib/passbook';
import { mgmKeys, saveMgmCampaign, useMgmCampaigns } from '../api/use-mgm-admin';
import {
  campaignToForm,
  emptyForm,
  formToPayload,
  formToPublicRules,
  needsPurchaseRules,
  validateIdentity,
  validatePurchase,
  validateRewards,
  type CampaignFormState,
  type RewardTrigger,
  type RoleForm,
} from '../model/campaign-form';
import { amountLabel, formatReward, programLabel } from '../model/program';
import type { MgmProgram, MgmRole } from '../model/types';
import { MgmPreview } from './mgm-preview';
import { ROLE_ICON, TRIGGER_ICON } from './status-icons';

function Field({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function SwitchRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] p-3">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

const ROLE_COPY: Record<MgmRole, { title: string; motion: IconMotion; description: string }> = {
  referrer: { title: 'Quem indica', motion: 'wiggle', description: 'O cliente que compartilhou o link.' },
  referred: { title: 'Quem é indicado', motion: 'float', description: 'O amigo que entrou pelo link.' },
};

// Opções dos seletores com ícone animado (criados uma vez, fora do render).
const TRIGGER_OPTIONS: SegmentedOption<RewardTrigger>[] = [
  { value: 'registered', label: 'No cadastro', icon: animatedIcon(TRIGGER_ICON.registered, 'pop') },
  { value: 'first_purchase', label: 'Na 1ª compra', icon: animatedIcon(TRIGGER_ICON.first_purchase, 'bounce') },
];
const VALIDATION_OPTIONS: SegmentedOption<'hold' | 'immediate'>[] = [
  { value: 'hold', label: 'Com carência', icon: animatedIcon(TRIGGER_ICON.hold, 'flip') },
  { value: 'immediate', label: 'Na hora', icon: animatedIcon(TRIGGER_ICON.immediate, 'pulse') },
];

function toast422(errors: string[]): boolean {
  if (errors.length === 0) return true;
  toast.error(errors[0]);
  return false;
}

/**
 * Campanha do Indique e Ganhe (só master). Quatro passos: identificação,
 * recompensa de cada lado, validação da 1ª compra e textos — com a prévia do
 * que o cliente vê atualizando ao vivo.
 */
export function MgmCampaignFormPage() {
  const { campaignId } = useParams<{ campaignId?: string }>();
  const { idPassbook, passbook } = usePassbook();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const campaigns = useMgmCampaigns(idPassbook);
  const program: MgmProgram | null = campaigns.data?.program ?? null;
  const existing = useMemo(
    () => (campaignId ? campaigns.data?.campaigns.find((c) => c._id === campaignId) ?? null : null),
    [campaignId, campaigns.data],
  );

  const [form, setForm] = useState<CampaignFormState>(() => emptyForm());
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const back = `/loja/${idPassbook}/indique-e-ganhe`;

  useEffect(() => {
    if (existing && loadedId !== existing._id) {
      setForm(campaignToForm(existing));
      setLoadedId(existing._id);
    }
  }, [existing, loadedId]);

  const set = <K extends keyof CampaignFormState>(key: K, value: CampaignFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const setRole = (role: MgmRole, patch: Partial<RoleForm>) =>
    setForm((prev) => ({ ...prev, [role]: { ...prev[role], ...patch } }));

  const previewRules = useMemo(
    () => (program ? formToPublicRules(form, program, formatReward) : null),
    [form, program],
  );

  if (campaigns.isLoading) return <Skeleton className="h-96 rounded-2xl" />;
  if (!program) {
    return (
      <EmptyState
        icon={Gift}
        title="Programa sem suporte ao Indique e Ganhe"
        description="Cartões de saldo pré-pago e gift card não participam."
      />
    );
  }
  if (campaignId && !existing) {
    return <EmptyState icon={Gift} title="Campanha não encontrada" action={<Button onClick={() => navigate(back)}>Voltar</Button>} />;
  }

  const save = async () => {
    const errors = [...validateIdentity(form), ...validateRewards(form, program), ...validatePurchase(form)];
    if (!toast422(errors)) return;
    setSaving(true);
    try {
      await saveMgmCampaign(idPassbook, existing?._id ?? null, formToPayload(form, program));
      await queryClient.invalidateQueries({ queryKey: mgmKeys.all(idPassbook) });
      toast.success(existing ? 'Campanha atualizada.' : 'Campanha criada.');
      navigate(back);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível salvar a campanha.');
    } finally {
      setSaving(false);
    }
  };

  const roleCard = (role: MgmRole) => {
    const r = form[role];
    const copy = ROLE_COPY[role];
    return (
      <Card key={role} className={cn('space-y-4 p-4', !r.enabled && 'opacity-70')}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 font-medium">
              <span className="grid size-8 place-items-center rounded-lg bg-primary/12 text-primary">
                <AnimatedIcon icon={ROLE_ICON[role]} motion={copy.motion} loop className="size-4" />
              </span>
              {copy.title}
            </p>
            <p className="text-xs text-muted-foreground">{copy.description}</p>
          </div>
          <Switch checked={r.enabled} onCheckedChange={(v) => setRole(role, { enabled: v })} aria-label={`Recompensa de ${copy.title}`} />
        </div>
        {r.enabled ? (
          <>
            <Field label="Quando ganha?">
              <SegmentedControl<RewardTrigger>
                value={r.trigger}
                onChange={(v) => setRole(role, { trigger: v })}
                options={TRIGGER_OPTIONS}
                fill
                size="sm"
              />
            </Field>
            <Field
              label={amountLabel(program)}
              htmlFor={`mgm-amount-${role}`}
              hint={`Usa a mecânica do programa (${programLabel(program).toLowerCase()}). Nenhum saldo novo é criado.`}
            >
              <Input
                id={`mgm-amount-${role}`}
                inputMode={program === 'cashback' ? 'decimal' : 'numeric'}
                value={r.amount}
                onChange={(e) => setRole(role, { amount: e.target.value })}
                placeholder={program === 'cashback' ? '10,00' : '1'}
              />
            </Field>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Sem recompensa para este lado.</p>
        )}
      </Card>
    );
  };

  const purchase = needsPurchaseRules(form);

  const steps: WizardStep[] = [
    {
      id: 'identity',
      title: 'Identificação',
      icon: IdCard,
      validate: () => toast422(validateIdentity(form)),
      content: (
        <div className="space-y-4">
          <Field label="Nome da campanha" htmlFor="mgm-name">
            <Input id="mgm-name" value={form.name} maxLength={80} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Início">
              <DatePicker value={form.startsAt} onChange={(v) => set('startsAt', v)} />
            </Field>
            <Field label="Término" hint="Opcional. Sem término, vale até ser desativada.">
              <DatePicker value={form.endsAt} onChange={(v) => set('endsAt', v)} placeholder="Sem término" />
            </Field>
          </div>
          {form.endsAt ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => set('endsAt', '')}>
              Remover data de término
            </Button>
          ) : null}
          <SwitchRow
            title="Ativa"
            description="Uma campanha ativa por loja: ativar esta desativa a anterior. Indicações já cadastradas terminam com as regras que tinham."
            checked={form.active}
            onChange={(v) => set('active', v)}
          />
        </div>
      ),
    },
    {
      id: 'rewards',
      title: 'Recompensas',
      icon: Gift,
      validate: () => toast422(validateRewards(form, program)),
      content: (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Cada lado tem a própria recompensa e o próprio gatilho. Ex.: o amigo ganha no cadastro e quem indicou só
            quando o amigo compra.
          </p>
          <div className="grid gap-4 lg:grid-cols-2">{(['referrer', 'referred'] as const).map(roleCard)}</div>
        </div>
      ),
    },
    {
      id: 'purchase',
      title: 'Primeira compra',
      icon: ShieldCheck,
      validate: () => toast422(validatePurchase(form)),
      content: purchase ? (
        <div className="space-y-4">
          <Field label="Como validar a primeira compra">
            <SegmentedControl
              value={form.validationMode}
              onChange={(v) => set('validationMode', v)}
              options={VALIDATION_OPTIONS}
              fill
              size="sm"
            />
          </Field>
          {form.validationMode === 'hold' ? (
            <Field
              label="Carência (horas)"
              htmlFor="mgm-hold"
              hint="A compra só conta depois deste tempo sem cancelamento ou estorno. Protege contra compra-e-cancela."
            >
              <Input id="mgm-hold" inputMode="numeric" value={form.holdHours} onChange={(e) => set('holdHours', e.target.value)} />
            </Field>
          ) : (
            <>
              <SwitchRow
                title="Estornar se a compra for cancelada"
                description="A recompensa sai na hora; se a compra cair dentro da janela, lançamos um débito compensatório."
                checked={form.clawback}
                onChange={(v) => set('clawback', v)}
              />
              {form.clawback ? (
                <Field label="Janela de estorno (dias)" htmlFor="mgm-clawback">
                  <Input id="mgm-clawback" inputMode="numeric" value={form.clawbackDays} onChange={(e) => set('clawbackDays', e.target.value)} />
                </Field>
              ) : null}
            </>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Valor mínimo da compra (R$)" htmlFor="mgm-min" hint="Opcional.">
              <Input id="mgm-min" inputMode="decimal" value={form.minAmount} onChange={(e) => set('minAmount', e.target.value)} placeholder="Sem mínimo" />
            </Field>
            <Field label="Prazo após o cadastro (dias)" htmlFor="mgm-within" hint="Opcional. Depois disso a recompensa de compra é cancelada.">
              <Input id="mgm-within" inputMode="numeric" value={form.withinDays} onChange={(e) => set('withinDays', e.target.value)} placeholder="Sem prazo" />
            </Field>
          </div>
        </div>
      ) : (
        <p className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
          Nenhum lado depende de compra — as recompensas saem no cadastro. Nada a configurar aqui.
        </p>
      ),
    },
    {
      id: 'copy',
      title: 'Textos',
      icon: MessageSquareText,
      content: (
        <div className="space-y-4">
          <Field label="Título da página" htmlFor="mgm-title">
            <Input id="mgm-title" value={form.title} maxLength={60} onChange={(e) => set('title', e.target.value)} />
          </Field>
          <Field label="Subtítulo" htmlFor="mgm-subtitle">
            <Textarea id="mgm-subtitle" rows={2} value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} />
          </Field>
          <Field label="Notificação para quem indicou" htmlFor="mgm-push-referrer" hint="Use {amount} (recompensa) e {name} (quem entrou).">
            <Textarea id="mgm-push-referrer" rows={2} value={form.referrerPush} onChange={(e) => set('referrerPush', e.target.value)} />
          </Field>
          <Field label="Notificação para quem foi indicado" htmlFor="mgm-push-referred" hint="Use {amount} e {name} (quem indicou).">
            <Textarea id="mgm-push-referred" rows={2} value={form.referredPush} onChange={(e) => set('referredPush', e.target.value)} />
          </Field>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={existing ? 'Editar campanha' : 'Nova campanha'}
        description="Indique e ganhe — só o master configura."
        actions={
          <Button variant="secondary" onClick={() => navigate(back)}>
            <ArrowLeft className="size-4" /> Voltar
          </Button>
        }
      />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Card className="p-4 sm:p-6">
          <Wizard
            steps={steps}
            onFinish={save}
            onCancel={() => navigate(back)}
            isSubmitting={saving}
            finishLabel={existing ? 'Salvar campanha' : 'Criar campanha'}
            clickableStepper
          />
        </Card>
        <aside className="xl:sticky xl:top-4 xl:self-start">
          {previewRules ? (
            <MgmPreview
              passbook={passbook}
              rules={previewRules}
              referrerPush={form.referrerPush}
              referredPush={form.referredPush}
            />
          ) : null}
        </aside>
      </div>
    </>
  );
}
